import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { DataContext } from "../context/dataContext";
import { Typography, Button, Paper, TextField, FormControlLabel, Checkbox, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { withStyles } from '@material-ui/core/styles';

const BlackCheckbox = withStyles({
    root: {
        color: 'black',
        '&$checked': { color: 'black' },
    },
    checked: {},
})((props) => <Checkbox color="default" {...props} />);

const useStyles = makeStyles(() => ({
  aaBox: {
    width: 44, height: 44,
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: 6, border: "1px solid #ccc",
    fontWeight: 600, fontSize: "1rem"
  }
}));

export default function BystanderSetup() {
  const { textAreaValue } = useContext(DataContext);
  const history = useHistory();
  const classes = useStyles();

  const [sequence, setSequence] = useState(textAreaValue || "");
  const [orfResult, setOrfResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const checkOrf = () => {
    setLoading(true);
    setError(null);
    setOrfResult(null);
    setConfirmed(false);
    axios.post(`${API_URL}check_orf`, { strand: sequence })
      .then(res => { setOrfResult(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || "Error checking ORF"); setLoading(false); });
  };

  const runBystander = () => {
    setSubmitting(true);
    axios.post(`${API_URL}ml_batch`, { strand: sequence })
        .then(res => history.push(`/batch/${res.data.job_id}`))
        .catch(err => {
            setError(err.response?.data?.error || "Failed to submit job")
            setSubmitting(false)
        });
  };

  const AARow = ({ label, data, highlight }) => (
    <div style={{ marginBottom: "1.5rem" }}>
      <Typography variant="body2" style={{ color: "grey", marginBottom: "0.5rem", textAlign: "center" }}>{label}</Typography>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
        {data.left_flanking.split('').map((aa, i) => (
          <div key={`l${i}`} className={classes.aaBox} style={{ backgroundColor: "#DEEBF7" }}>{aa}</div>
        ))}
        {data.left_flanking && <div style={{ width: 2, height: 44, backgroundColor: "#bbb", margin: "0 4px" }} />}
        {data.edited.split('').map((aa, i) => (
          <div key={`e${i}`} className={classes.aaBox} style={{ backgroundColor: highlight }}>{aa}</div>
        ))}
        {data.right_flanking && <div style={{ width: 2, height: 44, backgroundColor: "#bbb", margin: "0 4px" }} />}
        {data.right_flanking.split('').map((aa, i) => (
          <div key={`r${i}`} className={classes.aaBox} style={{ backgroundColor: "#DEEBF7" }}>{aa}</div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: "3%", padding: "0 5%" }}>
      <Paper elevation={4} style={{ padding: "3% 5%", borderRadius: 10, maxWidth: 800, width: "100%" }}>

        <Typography variant="h5" align="center" style={{ marginBottom: "4%" }}>
          Silent Bystander Analysis
        </Typography>

        <Typography variant="body2" style={{ marginBottom: "1.5rem", color: "#555" }}>
            Silent bystander analysis predicts pegRNAs that include synonymous mutations flanking
            your edit, which can boost prime editing efficiency — especially in MMR+ contexts.
            For detailed guidance, see our{" "}
            <a href="https://rdcu.be/eAIEQ" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
                Nature Protocols
            </a>{" "}article.<br /><br />
            <strong>Requirements:</strong> Replacement edits only (1bp or multi-bp).
            Sequence must be <strong>in-frame</strong> (ORF starts at position 1)
            and have <strong>≥100bp</strong> flanking on each side of the edit.<br /><br />
            <strong>Note:</strong> The web tool uses <strong>1</strong> flanking amino acid on each side of the edit.
            Because bystander analysis evaluates orders of magnitude more pegRNA candidates than standard prediction,
            it requires significant cloud resources. This tool is intended for <strong>individual sequences</strong> only —
            for high-throughput use or screening, please run locally via the{" "}
            <a href="https://github.com/uzh-dqbm-cmi/PRIDICT2?tab=readme-ov-file#71-prediction-of-pegrnas-with-silent-bystander-edits"
                target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
                GitHub repository
            </a>.
            A daily usage cap applies to keep the service sustainable — if the limit is reached, you can always run the analysis locally via the GitHub link above.
        </Typography>

        <TextField
          fullWidth multiline minRows={6}
          style={{ backgroundColor: "#E4E4E4", marginBottom: "1rem" }}
          InputProps={{ disableUnderline: true }}
          variant="filled"
          placeholder="Enter your sequence (same format as main page, ≥100bp flanking required)"
          value={sequence}
          onChange={e => { setSequence(e.target.value); setOrfResult(null); setError(null); setConfirmed(false); }}
        />

        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.5rem" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#DEEBF7", textTransform: "none" }}
            onClick={checkOrf}
            disabled={loading || !sequence}
          >
            {loading ? <CircularProgress size={20} /> : "Check ORF"}
          </Button>
        </div>

        {error && (
          <Paper elevation={2} style={{ padding: "1rem", backgroundColor: "#FFEBEE", marginBottom: "1.5rem" }}>
            <Typography variant="body1" style={{ color: "#c62828" }}>⚠ {error}</Typography>
            {(error.includes("Insertion") || error.includes("Deletion")) && (
              <Typography variant="body2" style={{ marginTop: "0.5rem" }}>
                Silent bystander analysis only supports replacement edits.
                Please go back and run the standard prediction instead.
              </Typography>
            )}
          </Paper>
        )}

        {orfResult && (
          <>
            <Paper elevation={2} style={{ padding: "1.5rem", backgroundColor: "#F5F5F5", marginBottom: "1.5rem" }}>
              <Typography variant="h6" align="center" style={{ marginBottom: "1rem" }}>
                Affected Amino Acids
              </Typography>

              <div style={{ display: "flex", justifyContent: "center", gap: "2rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: 20, height: 20, backgroundColor: "#DEEBF7", border: "1px solid #ccc", borderRadius: 4 }} />
                  <Typography variant="body2">Flanking AAs (silent bystander window)</Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: 20, height: 20, backgroundColor: "#FFF9C4", border: "1px solid #ccc", borderRadius: 4 }} />
                  <Typography variant="body2">Edited AA(s) — before editing</Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: 20, height: 20, backgroundColor: "#FBE5D6", border: "1px solid #ccc", borderRadius: 4 }} />
                  <Typography variant="body2">Edited AA(s) — after editing</Typography>
                </div>
              </div>

              <AARow label="Before editing:" data={orfResult.original_AA} highlight="#FFF9C4" />
              <AARow label="After editing:" data={orfResult.edited_AA} highlight="#FBE5D6" />

              <Typography variant="body2" align="center" style={{ color: "#555", marginTop: "0.5rem" }}>
                Edit: <strong>{orfResult.edit_before} → {orfResult.edit_after}</strong> ({orfResult.mutation_type})
              </Typography>
            </Paper>

            <Paper elevation={2} style={{ padding: "1rem", backgroundColor: "#F9FBE7", marginBottom: "1.5rem" }}>
              <Typography variant="body2" style={{ color: "#555" }}>
                <strong>Please verify:</strong> The amino acids above should match your gene's actual sequence at the edit site.
                If they look incorrect, the sequence may not be in the correct reading frame —
                try shifting the sequence start by 1 or 2 bases.
              </Typography>
            </Paper>

            <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
              <FormControlLabel
                    control={<BlackCheckbox checked={confirmed} onChange={() => setConfirmed(c => !c)} />}
                    label="I confirm the reading frame is correct"
                />
            </div>

            {confirmed && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {submitting ? (
                    <div style={{ textAlign: "center" }}>
                        <CircularProgress size={24} style={{ marginBottom: "0.5rem" }} />
                        <Typography variant="body2" style={{ color: "grey" }}>
                            Preparing job (~20 seconds)...
                        </Typography>
                    </div>
                ) : (
                    <Button
                        size="large" variant="contained"
                        style={{ backgroundColor: "#E2EFDA", textTransform: "none" }}
                        onClick={runBystander}
                    >
                        Run Bystander Analysis
                    </Button>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <Button variant="text" style={{ textTransform: "none", color: "grey" }} onClick={() => history.push("/")}>
            ← Back to main page
          </Button>
        </div>

      </Paper>
    </div>
  );
}